import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { useAuth } from "../auth";
import { useCart } from "../cart";
import { useSeo } from "../ui";
import { isBuyable } from "./Products";

/* 무료배송 기준 — 결제 연동 시 서버 값으로 바꾼다 */
export const SHIPPING_FEE = 3000;
export const FREE_SHIPPING_OVER = 50000;

export function shippingFor(subtotal) {
  return subtotal === 0 || subtotal >= FREE_SHIPPING_OVER ? 0 : SHIPPING_FEE;
}

function Thumb({ src, name }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="cart-item__thumb">
      {src && !failed ? (
        <img src={src} alt={name} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <Icon.Box width="22" height="22" aria-hidden="true" focusable="false" />
      )}
    </div>
  );
}
export { Thumb as CartThumb };

/* 장바구니·주문서는 로그인한 회원만 — 로그인 전이면 안내를 보여준다 */
export function LoginRequired({ title }) {
  const { askLogin } = useCart();
  return (
    <section className="section shop">
      <div className="shop__head"><h1 className="shop__title">{title}</h1></div>
      <div className="shop-empty">
        <Icon.Cart width="30" height="30" aria-hidden="true" focusable="false" />
        <p>로그인 후 이용해 주세요.<br />장바구니 담기와 구매는 회원만 할 수 있습니다.</p>
        <button type="button" className="btn btn--primary" onClick={askLogin}>로그인하기</button>
      </div>
    </section>
  );
}

/* 펌웨어 개발·빌드 줄 — 라오나봇 내 프로젝트의 [펌웨어 빌드 포함]으로 담긴다. 수량은 1로 고정 */
export function FwLine({ row, onRemove, locked }) {
  const editable = !!onRemove || locked; // 장바구니 줄 (주문서에서는 static)
  return (
    <div className={`cart-item cart-item--fw${editable ? "" : " cart-item--static"}`}>
      <div className="cart-item__thumb cart-item__thumb--fw" aria-hidden="true">FW</div>
      <div className="cart-item__info">
        <div className="shop-card__cat">펌웨어 개발의뢰 · {row.no}</div>
        <span className="cart-item__name">펌웨어 개발·빌드 — {row.title}</span>
        <div className="cart-item__unit">
          기본 비용 · 코드 전체(바이너리 포함) 제공{locked ? " · 라오나봇 프로젝트는 함께 주문합니다" : ""}
        </div>
      </div>
      {editable ? <div className="qty cart-item__fwqty">1건</div> : null}
      <div className="cart-item__sum">{(row.fee || 0).toLocaleString("ko-KR")}원</div>
      {onRemove && (
        <button type="button" className="cart-item__remove" onClick={onRemove} aria-label="펌웨어 개발·빌드 빼기">×</button>
      )}
      {!onRemove && locked && <span aria-hidden="true" />}
    </div>
  );
}

/* 장바구니 목록 + 합계 — 장바구니 페이지와 MY 페이지가 함께 쓴다 */
export function CartPanel() {
  const navigate = useNavigate();
  const { items, setQty, remove, clear, total, fw, fwTotal, removeFw } = useCart();
  const shipping = shippingFor(total);

  return (
    <>
      {items.length === 0 && fw.length === 0 ? (
        <div className="shop-empty">
          <Icon.Cart width="30" height="30" aria-hidden="true" focusable="false" />
          <p>장바구니가 비어 있습니다.</p>
          <Link to="/products" className="btn btn--primary">판매제품 보러가기</Link>
        </div>
      ) : (
        <div className="cart">
          <div className="cart__list">
            <div className="cart__bar">
              <span>상품 {items.length}종{fw.length ? ` · 펌웨어 개발 ${fw.length}건` : ""}</span>
              <button type="button" className="cart__clear" onClick={clear}>전체 삭제</button>
            </div>
            {items.map((i) => (
              <div key={i.slug} className="cart-item">
                <Link to={`/products/${i.slug}`}><Thumb src={i.thumb} name={i.name} /></Link>
                <div className="cart-item__info">
                  {i.category && <div className="shop-card__cat">{i.category}</div>}
                  <Link to={`/products/${i.slug}`} className="cart-item__name">{i.name}</Link>
                  <div className="cart-item__unit">{(i.price || 0).toLocaleString("ko-KR")}원</div>
                </div>
                <div className="qty qty--sm">
                  <button type="button" onClick={() => setQty(i.slug, i.qty - 1)} disabled={i.qty <= 1} aria-label="수량 줄이기">−</button>
                  <input
                    type="number"
                    min="1"
                    value={i.qty}
                    onChange={(e) => setQty(i.slug, parseInt(e.target.value, 10) || 1)}
                    aria-label={`${i.name} 수량`}
                  />
                  <button type="button" onClick={() => setQty(i.slug, i.qty + 1)} aria-label="수량 늘리기">+</button>
                </div>
                <div className="cart-item__sum">{((i.price || 0) * i.qty).toLocaleString("ko-KR")}원</div>
                <button type="button" className="cart-item__remove" onClick={() => remove(i.slug)} aria-label={`${i.name} 삭제`}>
                  ×
                </button>
              </div>
            ))}
            {fw.map((r) => <FwLine key={`fw${r.id}`} row={r} onRemove={r.locked ? null : () => removeFw(r.id)} locked={r.locked} />)}
          </div>

          <aside className="cart__summary">
            <div className="cart__row"><span>상품금액</span><span>{total.toLocaleString("ko-KR")}원</span></div>
            {fwTotal > 0 && (
              <div className="cart__row"><span>펌웨어 개발·빌드</span><span>{fwTotal.toLocaleString("ko-KR")}원</span></div>
            )}
            <div className="cart__row">
              <span>배송비</span>
              <span>{shipping === 0 ? "무료" : `${shipping.toLocaleString("ko-KR")}원`}</span>
            </div>
            {shipping > 0 && total > 0 && (
              <p className="cart__hint">
                {(FREE_SHIPPING_OVER - total).toLocaleString("ko-KR")}원 더 담으면 무료배송
              </p>
            )}
            <div className="cart__row cart__row--total">
              <span>결제 예정 금액</span>
              <strong>{(total + shipping + fwTotal).toLocaleString("ko-KR")}원</strong>
            </div>
            <button type="button" className="btn btn--primary btn--block" onClick={() => navigate("/checkout")}>
              주문하기
            </button>
          </aside>
        </div>
      )}
    </>
  );
}

/* 상품코드로 한꺼번에 담기 — /cart?add=CODE:수량,CODE:수량
   내 프로젝트(라오나봇)의 '주문하기'가 보낸다. 판매 중이고 가격이 있는 상품만 담고, 나머지는 알려 준다.
   코드에는 공백 · 특수문자가 들어갈 수 있어 코드마다 encodeURIComponent 로 한 번 더 감싸 보낸다 (, : 가 있어도 되게).
   대소문자는 가리지 않는다. 처리한 뒤에는 주소에서 add 를 지워 새로고침해도 두 번 담기지 않게 한다. */
// 1–40자, 공백만은 안 되고 제어 문자(줄바꿈 등)는 쓸 수 없다 — app/laona.py 와 같은 규칙
const okCode = (c) => [...c].length <= 40 && /\S/.test(c) && ![...c].some((ch) => ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127);

function parseAdd(raw) {
  const out = new Map();
  for (const part of String(raw || "").split(",").slice(0, 60)) {
    const i = part.lastIndexOf(":");
    const c = i >= 0 ? part.slice(0, i) : part, q = i >= 0 ? part.slice(i + 1) : "";
    let code = c;
    try { code = decodeURIComponent(c); } catch { /* 예전 주소(인코딩 안 한 코드)는 그대로 */ }
    code = code.trim().toUpperCase();
    const qty = Math.max(1, Math.min(99, parseInt(q, 10) || 1));
    if (okCode(code)) out.set(code, Math.min(99, (out.get(code) || 0) + qty));
  }
  return out;
}

function useImportByCode(enabled) {
  const location = useLocation();
  const navigate = useNavigate();
  const { add, addProject, addFw } = useCart();
  const [report, setReport] = useState(location.state?.imported || null);
  const done = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("add");
    const fwId = Number(params.get("fw"));
    const wantFw = Number.isInteger(fwId) && fwId > 0;
    if (!enabled || (!raw && !wantFw) || done.current) return;
    done.current = true;
    const wanted = parseAdd(raw);
    const from = { laonabot: "라오나봇 연결도", project: "프로젝트", bot: "라오나봇 프로젝트" }[params.get("from")] || "";
    (async () => {
      let byCode = new Map();
      try {
        const res = await fetch("/api/products");
        const data = await res.json();
        byCode = new Map((data.items || []).filter((p) => p.code).map((p) => [p.code.trim().toUpperCase(), p]));
      } catch { /* 목록을 못 받으면 모두 '담지 못함'으로 알린다 */ }
      const added = [];
      const skipped = [];
      for (const [code, qty] of wanted) {
        const p = byCode.get(code);
        if (p && isBuyable(p)) {
          add(p, qty);
          added.push({ code, name: p.name, qty });
        } else {
          skipped.push({ code, why: !p ? "판매 중인 상품이 없음" : p.status === "SOLDOUT" ? "품절" : "가격 문의 상품" });
        }
      }
      /* 라오나봇 수동 프로젝트에서 담았으면 주문서가 주문을 그 프로젝트에 잇는다 (프로젝트 자료 다운로드가 열린다) */
      const project = Number(params.get("project"));
      const fromProject = ["project", "bot"].includes(params.get("from")) && Number.isInteger(project) && project > 0;
      if (fromProject && params.get("from") === "project" && added.length) addProject(project); // 수동 프로젝트 — 자료 다운로드
      /* [펌웨어 빌드 포함] — 아직 주문하지 않은 이 프로젝트의 개발의뢰만 담는다 */
      let fwAdded = null;
      if (fromProject && wantFw) {
        try {
          const res = await fetch(`/api/laona/fw/requests/${fwId}`, { credentials: "same-origin" });
          const d = res.ok ? await res.json() : null;
          const r = d && d.request;
          if (r && r.status === "REQUESTED") {
            // locked — 라오나봇 프로젝트의 빌드비는 장바구니에서 따로 뺄 수 없다
            fwAdded = { id: r.id, no: r.request_no, title: r.title, fee: d.fee, locked: !!d.locked };
            addFw(fwAdded);
          } else {
            skipped.push({ code: "펌웨어 개발의뢰", why: "이미 주문했거나 찾을 수 없음" });
          }
        } catch {
          skipped.push({ code: "펌웨어 개발의뢰", why: "불러오지 못함" });
        }
      }
      const result = { from, added, skipped, fw: fwAdded };
      setReport(result);
      navigate("/cart", { replace: true, state: { imported: result } });
    })();
  }, [enabled, location.search, add, addProject, addFw, navigate]);

  return report;
}

function ImportNotice({ report }) {
  if (!report) return null;
  const n = report.added.reduce((s, a) => s + a.qty, 0);
  return (
    <div className="cart-import" role="status">
      {report.added.length > 0 ? (
        <p><strong>{report.from ? `${report.from}의 ` : ""}부품 {report.added.length}종 {n}개를 장바구니에 담았습니다.</strong></p>
      ) : report.fw ? null : (
        <p><strong>담을 수 있는 상품이 없었습니다.</strong></p>
      )}
      {report.fw && <p><strong>펌웨어 개발·빌드({report.fw.no})를 장바구니에 담았습니다.</strong></p>}
      {report.skipped.length > 0 && (
        <p className="cart-import__skip">
          담지 못한 상품코드: {report.skipped.map((s) => `${s.code} (${s.why})`).join(", ")}
        </p>
      )}
    </div>
  );
}

export default function Cart() {
  useSeo({ title: "장바구니 | I.META", description: "담아 둔 상품", path: "/cart", noindex: true });
  const { member, ready } = useAuth();
  const report = useImportByCode(ready && !!member);

  if (!ready) return <section className="section shop"><p className="note-line">불러오는 중…</p></section>;
  if (!member) return <LoginRequired title="장바구니" />;

  return (
    <section className="section shop">
      <div className="shop__head">
        <h1 className="shop__title">장바구니</h1>
        <Link to="/products" className="shop__cartlink">← 쇼핑 계속하기</Link>
      </div>
      <ImportNotice report={report} />
      <CartPanel />
    </section>
  );
}
